import { Box, IconButton, Pagination, Paper, Select, styled, Switch, TextField, Typography } from '@mui/material';
import Slider from '@mui/material/Slider';
import { motion } from 'framer-motion';

export const ActivityToolbarContainer = styled(motion.div)(({ theme }) => ({
  backgroundColor: theme.palette.neutral.darkBlueHigh,
  display: 'flex',
  borderRadius: '10px',
  alignItems: 'center',
  gap: '5px',
  height: '45px',
  width: '100%',
}));

export const ToolbarContainer = styled(Paper)({
  display: 'flex',
  alignItems: 'center',
  background: '#0A194E',
  color: '#FFF',
  padding: '8px 10px',
  borderRadius: '8px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
  cursor: 'grab',
  width: 'fit-content',
  gap: '10px',
});

export const VerticalDivider = styled(Box)(({ theme }) => ({
  marginLeft: 2,
  marginRight: 2,
  width: 1,
  height: 22,
  position: 'relative',
  '&::before': {
    content: '" "',
    height: 22,
    position: 'absolute',
    top: '0%',
    width: 1,
    backgroundColor: theme.palette.neutral.n200,
    marginBottom: -4,
  },
}));

// Styled Components
export const PaginationContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.neutral.white,
  borderRadius: '12px',
  color: '#ffffff',
  gap: '10px',
}));

export const PageNumber = styled(IconButton)(({ theme }) => ({
  width: 22,
  height: 22,
  borderRadius: '8px',
  fontSize: '14px',
  padding: '15px',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    padding: '15px',
  },
  '&.active': {
    backgroundColor: '#2952A3',
    padding: '15px',
  },
}));

export const PageButton = styled(IconButton)(({ theme }) => ({
  width: 22,
  height: 22,
  borderRadius: '8px',
  fontSize: '14px',
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#2952A3',
  },
  '&.active': {
    backgroundColor: '#2952A3',
  },
}));

export const Divider = styled('div')({
  width: '1px',
  height: '20px',
  backgroundColor: '#ffffff33',
});

export const StyledSelect = styled(Select)({
  borderRadius: '8px',
  padding: '8px 5px',
  height: '30px',
  color: '#9DA1B5',
  '& .MuiSelect-icon': {
    color: '#9DA1B5',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover': {
    // backgroundColor: '#2952A3',
  },
});

export const PaginationNumber = styled(Pagination)({
  '& .MuiPagination-ul': {
    flexWrap: 'nowrap',
  },
  '& .MuiPaginationItem-root': {
    color: '#9DA1B5',
    borderRadius: '8px',
    fontSize: '18px',
    minWidth: '15px', // Ensures the button inside <li> has a minimum width
    '& button': {
      minWidth: '15px', // Specifically targets the button inside the PaginationItem
    },
    '&:hover': {
      backgroundColor: '#0056b3',
    },
    '&.Mui-selected': {
      backgroundColor: '#0056b3',
      color: '#fff',
    },
  },
});

// Wrap Box with motion first
const MotionBox = motion('div');

// Now, style the motion-enabled Box
export const ToolbarElements = styled(MotionBox)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: 5,
  opacity: 0, // Start with opacity 0
  transform: 'translateX(-100%)', // Start off-screen to the left
});

// Styled Components
export const SearchContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#0A194E',
  color: '#fff',
  padding: '12px',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '25rem',
}));

export const SearchHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const StyledSwitch = styled(Switch)({
  '& .MuiSwitch-track': {
    backgroundColor: '#0057FF',
  },
});

export const SelectFieldContainer = styled(Box)({
  position: 'relative',
});

export const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '5px',
    backgroundColor: '#fff',
    padding: '5px',
    '& fieldset': {
      borderColor: '#D9E2EC',
    },
    '&:hover fieldset': {
      borderColor: '#A0AEC0',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3182CE',
    },
  },
});

export const HelperText = styled(Paper)({
  position: 'absolute',
  padding: '10px',
  backgroundColor: '#F7FAFC',
  color: '#A0AEC0',
  borderRadius: '8px',
  textAlign: 'center',
  fontSize: '14px',
  fontWeight: 500,
  marginTop: '4px',
});

export const DropdownContainer = styled(Paper)({
  position: 'absolute',
  width: '375px',
  backgroundColor: '#fff',
  borderRadius: '8px',
  padding: '12px',
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
  zIndex: 10,
  overflowY: 'auto',
});

export const FilterChips = styled(Box)({
  display: 'flex',
  gap: '6px',
  marginBottom: '8px',
});

export const CallItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 0',
});

export const CallText = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

export const HighlightedText = styled('span')({
  color: '#0057FF',
  fontWeight: 'bold',
});

export const PopperContainer = styled('div')<{ top: number; left: number }>(({ theme, top, left }) => ({
  top: `${top}px`,
  left: `${left}px`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  color: '#9DA1B5',
  zIndex: 1400,
  marginTop: '5px',
}));

export const InfoTabContainer = styled(Paper)(({ theme }) => ({
  marginTop: '5px',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.neutral.darkBlueHigh,
  color: '#FFF',
  borderRadius: '8px',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
  gap: '10px',
  whiteSpace: 'nowrap',
  padding: '8px 16px',
  width: 'fit-content',
}));

export const InfoTitle = styled(Typography)({
  color: '#fff',
  fontSize: '16px',
  fontWeight: 'bold',
  padding: '4px 8px',
  borderRadius: '4px',
});

export const Badge = styled(Box)({
  backgroundColor: '#2980b9',
  color: '#fff',
  fontSize: '12px',
  fontWeight: 'bold',
  padding: '2px 6px',
  borderRadius: '4px',
  marginTop: '4px',
  display: 'inline-block',
});

export const TimelineContainer = styled(Box)({
  backgroundColor: '#0D2040',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 20px',
  borderRadius: '10px',
  color: '#ffffff',
  position: 'relative',
  width: '100%',
});

export const StyledSlider = styled(Slider)({
  width: '100%',
  marginLeft: '20px',
  marginRight: '20px',
  color: '#4A4A6A',
  height: 8,
  '& .MuiSlider-thumb': {
    width: 18,
    height: 18,
    backgroundColor: '#ffffff',
    border: '2px solid #4A4A6A',
  },
  '& .MuiSlider-track': {
    height: 8,
  },
  '& .MuiSlider-rail': {
    height: 8,
    opacity: 1,
    backgroundColor: '#4A4A6A',
  },
  '& .MuiSlider-mark': {
    backgroundColor: '#4A4A6A',
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
});
